import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './Investors.css';

const Investors = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const topBarRef = useRef(null);

  // Language toggle handler
  const handleLanguageToggle = (lang) => {
    i18n.changeLanguage(lang);
  };

  // Scroll handler for top bar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission via mailto
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.message) {
      return;
    }

    // Format the email body
    const subject = encodeURIComponent(t('investors.form.emailSubject'));
    const body = encodeURIComponent(
      `${t('investors.form.emailBodyPrefix')}\n\n` +
      `${t('investors.form.nameLabel')}: ${formData.name}\n` +
      `${t('investors.form.emailLabel')}: ${formData.email}\n` +
      `${t('investors.form.companyLabel')}: ${formData.company || t('investors.form.notProvided')}\n` +
      `${t('investors.form.phoneLabel')}: ${formData.phone || t('investors.form.notProvided')}\n\n` +
      `${t('investors.form.messageLabel')}:\n${formData.message}`
    );

    // Create mailto link
    const mailtoLink = `mailto:contact@hedgit.ai?subject=${subject}&body=${body}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Show success message
    setShowSuccess(true);
    
    // Reset form after a delay
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: ''
      });
      setShowSuccess(false);
    }, 5000);
  };

  // Get current year for footer
  const currentYear = new Date().getFullYear();

  return (
    <div className="investors-wrapper">
      {/* Fixed Top Bar */}
      <nav 
        ref={topBarRef}
        className={`fixed-top-bar ${isScrolled ? 'scrolled' : ''}`}
      >
        <div className="top-bar-logo">
          <button
            className="language-toggle"
            aria-label={t('investors.topBar.toggleLanguage')}
            onClick={(e) => {
              const option = e.target.closest('.lang-option');
              if (option) {
                const targetLang = option.dataset.lang;
                if (targetLang) {
                  handleLanguageToggle(targetLang);
                }
              }
            }}
          >
            <span 
              className={`lang-option ${i18n.language === 'en' ? 'active' : ''}`} 
              data-lang="en"
            >
              EN
            </span>
            <span className="lang-separator">|</span>
            <span 
              className={`lang-option ${i18n.language === 'es' ? 'active' : ''}`} 
              data-lang="es"
            >
              ES
            </span>
          </button>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="86.609"
            height="77.109"
            viewBox="0 0 86.609 77.109"
          >
            <defs>
              <clipPath id={`clip-path-investors-${i18n.language}`}>
                <rect
                  width="42.305"
                  height="48.895"
                  transform="translate(-3 -6)"
                  fill="none"
                  stroke="#49a9ff"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </clipPath>
            </defs>
            <path
              d="M31.061,4.859,14.374,37.091.435,65.389c-1.175,2.385.114,4.365,2.866,4.4l27.76.34,27.76-.34c2.752-.034,4.041-2.013,2.866-4.4l-13.94-28.3Z"
              transform="translate(0 6.982)"
              fill="#49a9ff"
            />
            <g transform="translate(48.422 6)" style={{ isolation: 'isolate' }}>
              <g transform="translate(-1.119 0)">
                <g clipPath={`url(#clip-path-investors-${i18n.language})`}>
                  <path
                    d="M45.006,46.045,54.8,27.824l8.179-15.995a1.754,1.754,0,0,0-1.683-2.488L45.006,9.15l-16.289.191a1.754,1.754,0,0,0-1.681,2.488l8.179,15.995Z"
                    transform="translate(-26.854 -9.149)"
                    fill="none"
                    stroke="#49a9ff"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  />
                </g>
              </g>
            </g>
          </svg>
        </div>
        <div className="top-bar-buttons">
          <Link 
            to="/landing" 
            className="top-bar-button tertiary"
          >
            {t('investors.topBar.overview')}
          </Link>
          <Link 
            to="/signin" 
            className="top-bar-button secondary"
          >
            {t('investors.topBar.login')}
          </Link>
          <Link 
            to="/subscriptions" 
            className="top-bar-button primary"
          >
            {t('investors.topBar.getStarted')}
          </Link>
        </div>
      </nav>

      {/* Main Content Section */}
      <section className="investors-section">
        <article className="investors-card">
          <div className="investors-info">
            <img 
              src="/images/logos/logo.svg" 
              alt={t('investors.info.logoAlt')} 
              className="investors-info-logo" 
            />
            <p 
              className="eyebrow" 
              style={{
                letterSpacing: '0.4em',
                textTransform: 'uppercase',
                color: 'rgba(244, 247, 251, 0.7)',
                margin: 0
              }}
            >
              {t('investors.info.eyebrow')}
            </p>
            <h1>
              {t('investors.info.headline')}
            </h1>
            <p className="investors-description">{t('investors.info.description')}</p>
            <ul className="investors-highlights">
              {t('investors.info.highlights', { returnObjects: true }).map((highlight, index) => (
                <li key={index}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  {highlight}
                </li>
              ))}
            </ul>
          </div>
          <div className="investors-form">
            <div>
              <h2 className="form-title">{t('investors.form.title')}</h2>
              <p className="form-subtitle">{t('investors.form.subtitle')}</p>
            </div>
            {showSuccess && (
              <div className="form-success">
                {t('investors.form.successMessage')}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">{t('investors.form.nameLabel')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder={t('investors.form.namePlaceholder')}
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">{t('investors.form.emailLabel')} *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={t('investors.form.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="company">{t('investors.form.companyLabel')}</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  placeholder={t('investors.form.companyPlaceholder')}
                  value={formData.company}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">{t('investors.form.phoneLabel')}</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder={t('investors.form.phonePlaceholder')}
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">{t('investors.form.messageLabel')} *</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder={t('investors.form.messagePlaceholder')}
                  rows="6"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div style={{ marginTop: '32px' }}>
                <button type="submit" className="primary-button">
                  {t('investors.form.submitButton')}
                </button>
              </div>
            </form>
            <p className="investors-meta">
              {t('investors.form.meta')}
            </p>
          </div>
        </article>
      </section>

      {/* Footer */}
      <footer className="investors-footer">
        &copy; {currentYear} {t('investors.footer.copyright')}
        <br />
        {t('investors.footer.helpPrefix')}{' '}
        <a href="mailto:contact@hedgit.ai">contact@hedgit.ai</a>
      </footer>
    </div>
  );
};

export default Investors;

